import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import MediatorManager from "../../../core/application/MediatorManager";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import PullContentManager from "../../../ngt/pull/PullContentManager";
import ScenarioLibrary from "../../../ngt/scenario/ScenarioLibrary";
import ChapterConversationEvent from "../../chapters/event/ChapterConversationEvent";
import ChapterLibrary from "../../chapters/model/ChapterLibrary";
import MissionBriefingEvent from "../../mission-briefing/model/MissionBriefingEvent";
import { OnboardingStep } from "../../onboarding/model/OnboardingStep";
import ProgressModel from "../../progress/model/ProgressModel";
import ChapterHubEvent from "../event/ChapterHubEvent";
import ConversationTeaserEvent from "../event/ConversationTeaserEvent";
import ConversationTeaserPanelMediator from "./ConversationTeaserPanelMediator";
import ConversationTileMediator from "./ConversationTileMediator";

export default class ChapterHubScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {RevisableLanguagePack} languagePack 
     * @param {ScenarioLibrary} scenarioLibrary 
     * @param {AssetLibrary} assetLibrary
     * @param {PullContentManager} pullContentManager
     * @param {MediatorManager} mediatorManager
     * @param {ProgressModel} progressModel
     * @param {ChapterLibrary} chapterLibrary
     * @param {SoundManager} soundManager
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, languagePack, scenarioLibrary, assetLibrary, pullContentManager, mediatorManager, progressModel, chapterLibrary, soundManager) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._languagePack = languagePack;
        this._scenarioLibrary = scenarioLibrary;
        this._assetLibrary = assetLibrary;
        this._pullContentManager = pullContentManager;
        this._mediatorManager = mediatorManager;
        this._progressModel = progressModel;
        this._chapterLibrary = chapterLibrary;
        this._soundManager = soundManager;

        /** @type {string} */
        this._chapterId = this.getCustomData().chapterId;
        /** @type {boolean} */
        this._startHidden = this.getCustomData().startHidden;

        /** @type {ConversationTileMediator[]} */
        this._conversationTiles = [];
        this._currentPageIndex = 0;
    }

    async initialize() {
        super.initialize();

        if (this._startHidden) {
            this._target.visible = false;
            this._target.alpha = 0;
        }

        this._initializeConversationTiles();
        /** @type {ConversationTeaserPanelMediator} */
        this._conversationTeaserMediator = this._mediatorManager.createMediator(this._target.teaserPanel, SkinKeyRegistry.CONVERSATION_TEASER_COMPONENT);
        this._conversationTeaserMediator.hideInstant();

        this._previousButton = this.createButton(this._target.previousButton, this._handlePreviousButton.bind(this), SoundType.BUTTON_CLICK);
        this._previousButton.setVisible(false); // Content is now linear.
        this._nextButton = this.createButton(this._target.nextButton, this._handleNextButton.bind(this), SoundType.BUTTON_CLICK);
        this._nextButton.setVisible(false); // Content is now linear.
        this._coachButton = this.createButton(this._target.coachButton, this._handleCoachButton.bind(this), SoundType.BUTTON_CLICK);

        this.registerEventListener(ChapterHubEvent.SHOW, this._handleChapterHubShow.bind(this));
        this.registerEventListener(ChapterHubEvent.HIDE, this._handleChapterHubHide.bind(this));
        this.registerEventListener(ConversationTeaserEvent.SHOW, this._handleConversationTeaserShow.bind(this));
        this.registerEventListener(ConversationTeaserEvent.HIDE, this._handleConversationTeaserHide.bind(this));
        this.registerEventListener(ChapterConversationEvent.AVAILABLE, this._handleChapterConversationAvailable.bind(this));
        this.registerEventListener(ChapterConversationEvent.COMPLETED, this._handleChapterConversationCompleted.bind(this));

        this._updateNavigationButtons();
    }

    destroy() {
        super.destroy();

        // Clean up mediators.
        this._conversationTeaserMediator.destroy();
        this._conversationTeaserMediator = null;

        for (const conversationTile of this._conversationTiles) {
            conversationTile.destroy();
        }
        this._conversationTiles = null;
    }

    _initializeConversationTiles() {
        const scenarioId = this._scenarioLibrary.getCurrentScenario().id;
        const tileInstanceNamePrefix = 'tile_';
        for (const child of this._target.children) {
            if (child.name.indexOf(tileInstanceNamePrefix) !== 0) {
                continue;
            }

            const dialogId = child.name.substr(tileInstanceNamePrefix.length);
            const conversationTileSkin = child;
            conversationTileSkin.customData = {
                chapterId: this._chapterLibrary.getCurrentChapter().id,
                scenarioId,
                dialogId,
            };

            /** @type {ConversationTileMediator} */
            const mediator = this._mediatorManager.createMediator(conversationTileSkin, SkinKeyRegistry.CONVERSATION_TILE_COMPONENT);
            mediator.setEnabled(false);
            this._conversationTiles.push(mediator);

            // Hide all.
            mediator.hideInstant();
        }
    }

    _handlePreviousButton() {
        if (this._currentPageIndex <= 0) {
            return;
        }

        this._showConversationTile(this._currentPageIndex - 1);
    }

    _handleNextButton() {
        if (this._currentPageIndex >= this._conversationTiles.length - 1) {
            return;
        }

        this._showConversationTile(this._currentPageIndex + 1);
    }

    _handleCoachButton() {
        this.dispatch(MissionBriefingEvent.START);
    }

    _handleChapterHubShow() {
        this._show();
    }

    _handleChapterHubHide() {
        this._hide();
    }

    _handleConversationTeaserShow(evt) {
        /** @type {ConversationTeaserEvent} */
        const data = evt.params.data;
        this._conversationTeaserMediator.setTitle(data.title);
        this._conversationTeaserMediator.setDescription(data.description);
        this._conversationTeaserMediator.show();
    }

    _handleConversationTeaserHide() {
        this._conversationTeaserMediator.hide();
    }

    async _handleChapterConversationAvailable(evt) {
        const { dialogId } = evt.params.data;

        // Enable the tile.
        const conversationTile = this._conversationTiles.find((tile) => tile.dialogId === dialogId);
        conversationTile?.setEnabled(true);

        // Goto the tile.
        const conversationTileIndex = this._conversationTiles.findIndex((tile) => tile.dialogId === dialogId);
        if (conversationTileIndex >= 0) {
            this._showConversationTile(conversationTileIndex, false);
        }

        // Show the corresponding onboarding step.
        this._coachButton.setEnabled(false);
        await this.showAndAwaitOnboarding(OnboardingStep.SELECT_SCENARIO);
        this._coachButton.setEnabled(true);
    }

    _handleChapterConversationCompleted(evt) {
        const { dialogId } = evt.params.data;

        const conversationTile = this._conversationTiles.find((tile) => tile.dialogId === dialogId);
        conversationTile?.setEnabled(false);
    }

    async _show() {
        this.enableInteraction();

        this._target.visible = true;
        await createjs.Tween.get(this._target)
            .to({ alpha: 1 }, 450, createjs.Ease.sineInOut)
            .toPromise();
    }

    async _hide() {
        this.disableInteraction();

        await createjs.Tween.get(this._target)
            .to({ alpha: 0 }, 450, createjs.Ease.sineInOut)
            .toPromise();
        this._target.visible = true;
    }

    async _showConversationTile(index, animateIn = true) {
        const currentConversationTile = this._conversationTiles[this._currentPageIndex];
        const nextConversationTile = this._conversationTiles[index];

        if (animateIn) {
            const direction = index > this._currentPageIndex ? 1 : -1;
            currentConversationTile.hide(-direction, true);
            nextConversationTile.show(direction, true);
        } else {
            currentConversationTile.hideInstant();
            nextConversationTile.showInstant();
        }

        this._currentPageIndex = index;
        this._updateNavigationButtons();
    }

    _updateNavigationButtons() {
        this._previousButton.setEnabled(this._currentPageIndex > 0);
        this._nextButton.setEnabled(this._currentPageIndex < this._conversationTiles.length - 1);
    }
}